import type { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import { computed, type Ref } from 'vue'

export type AppMetricsComposable = ReturnType<typeof useAppMetrics>

export function useAppMetrics(app: Ref<EnrichedApp | null>) {
	const LaravelDeveloperHourlyRate = 48
	const VueDeveloperHourlyRate = 48 // Used to be 54, is now 44 because we have a joint sallary
	const CombinedDeveloperHourlyRate = Number(
		((VueDeveloperHourlyRate + LaravelDeveloperHourlyRate) / 2).toFixed(2),
	)
	const ConsultingRate = 100

	const totalConsultingValue = computed(() => {
		return ConsultingRate * 5
	})
	const totalDatabaseEstimate = computed(() => {
		return tablesEstimate.value
	})
	const totalDatabaseValue = computed(() => {
		return Number((totalDatabaseEstimate.value * (LaravelDeveloperHourlyRate / 60)).toFixed(2))
	})

	const totalApiEstimate = computed(() => {
		return (
			modelsEstimate.value +
			relationsEstimate.value +
			controllersEstimate.value +
			apiRoutesEstimate.value +
			requestsEstimate.value +
			factoriesEstimate.value +
			seedersEstimate.value
		)
	})
	const totalApiValue = computed(() => {
		return Number((totalApiEstimate.value * (LaravelDeveloperHourlyRate / 60)).toFixed(2))
	})

	const totalUiEstimate = computed(() => {
		return (
			listViewsEstimate.value +
			editViewsEstimate.value +
			formsEstimate.value +
			relationWidgetsEstimate.value +
			apiLayersEstimate.value +
			uiRoutesEstimate.value
		)
	})
	const totalUiValue = computed(() => {
		return Number((totalUiEstimate.value * (VueDeveloperHourlyRate / 60)).toFixed(2))
	})

	const totalDevEnvironmentEstimate = computed(() => {
		return (
			basicEnvironmentSetupEstimate.value +
			lintersConfigurationEstimate.value +
			formattersConfigurationEstimate.value +
			ciCdConfigurationEstimate.value +
			dockerConfigurationEstimate.value +
			kubernetesConfigurationEstimate.value +
			testingEnvironmentsConfigurationEstimate.value +
			environmentVariablesAndConfigConfigurationEstimate.value +
			authenticationConfigurationEstimate.value +
			readmeAndDocumentationEstimate.value
		)
	})
	const totalDevEnvironmentValue = computed(() => {
		return Number(
			(totalDevEnvironmentEstimate.value * (CombinedDeveloperHourlyRate / 60)).toFixed(2),
		)
	})

	const totalDevelopmentTime = computed(() => {
		return (
			totalDatabaseEstimate.value +
			totalApiEstimate.value +
			totalUiEstimate.value +
			totalDevEnvironmentEstimate.value
		)
	})

	const totalDevelopmentValue = computed(() => {
		return (
			totalDatabaseValue.value +
			totalApiValue.value +
			totalUiValue.value +
			totalDevEnvironmentValue.value
		).toFixed(2)
	})

	const MinutesPerTable = 30
	const tablesCount = computed(() => {
		if (!app.value) return 0
		let count = 0
		count += app.value.modules.length
		for (const module of app.value.modules) {
			for (const relationName in module.model.relations) {
				const relation = module.model.relations[relationName]!
				if (
					relation.relationType === 'BelongsToMany' &&
					relation.name.localeCompare(relation.inverse) < 0
				) {
					count += 1
				}
			}
		}
		return count
	})
	const tablesEstimate = computed(() => {
		return tablesCount.value * MinutesPerTable
	})

	const MinutesPerRelationWidget = 60
	const relationWidgetsCount = computed(() => {
		if (!app.value) return 0
		let count = 0
		for (const module of app.value.modules) {
			for (const relationName in module.model.relations) {
				const relation = module.model.relations[relationName]!
				if (
					relation.relationType === 'BelongsToMany' ||
					relation.relationType === 'HasMany' ||
					relation.relationType === 'HasOne'
				) {
					count += 1
				}
			}
		}
		return count
	})
	const relationWidgetsEstimate = computed(() => {
		return relationWidgetsCount.value * MinutesPerRelationWidget
	})

	const MinutesPerRelation = 10
	const relationsCount = computed(() => {
		if (!app.value) return 0
		let count = 0
		for (const module of app.value.modules) {
			count += Object.keys(module.model.relations).length
		}
		return count
	})
	const relationsEstimate = computed(() => {
		return relationsCount.value * MinutesPerRelation
	})

	const MinutesPerModel = 10
	const modelsCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const modelsEstimate = computed(() => {
		return modelsCount.value * MinutesPerModel
	})

	const MinutesPerController = 30
	const controllersCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const controllersEstimate = computed(() => {
		return controllersCount.value * MinutesPerController
	})

	const MinutesPerApiRoute = 10
	const apiRoutesCount = computed(() => {
		if (!app.value) return 0
		return (app.value.modules.length - 1) * 3
	})
	const apiRoutesEstimate = computed(() => {
		return apiRoutesCount.value * MinutesPerApiRoute
	})

	const MinutesPerRequest = 15
	const requestsCount = computed(() => {
		if (!app.value) return 0
		return (app.value.modules.length - 1) * 2
	})
	const requestsEstimate = computed(() => {
		return requestsCount.value * MinutesPerRequest
	})

	const MinutesPerFactory = 10
	const factoriesCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const factoriesEstimate = computed(() => {
		return factoriesCount.value * MinutesPerFactory
	})

	const MinutesPerSeeder = 10
	const seedersCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const seedersEstimate = computed(() => {
		return seedersCount.value * MinutesPerSeeder
	})

	const MinutesPerListView = 15
	const listViewsCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const listViewsEstimate = computed(() => {
		return listViewsCount.value * MinutesPerListView
	})

	const MinutesPerEditView = 15
	const editViewsCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const editViewsEstimate = computed(() => {
		return editViewsCount.value * MinutesPerEditView
	})

	const MinutesPerForm = 30
	const formsCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const formsEstimate = computed(() => {
		return formsCount.value * MinutesPerForm
	})

	const MinutesPerApiLayer = 15
	const apiLayersCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const apiLayersEstimate = computed(() => {
		return apiLayersCount.value * MinutesPerApiLayer
	})

	const MinutesPerUiRoute = 5
	const uiRoutesCount = computed(() => {
		if (!app.value) return 0
		return app.value.modules.length - 1
	})
	const uiRoutesEstimate = computed(() => {
		return uiRoutesCount.value * MinutesPerUiRoute
	})

	const MinutesPerBasicEnvironmentSetup = 120
	const basicEnvironmentSetupEstimate = computed(() => {
		return MinutesPerBasicEnvironmentSetup
	})

	const MinutesPerLintersConfiguration = 60
	const lintersConfigurationEstimate = computed(() => {
		return MinutesPerLintersConfiguration
	})

	const MinutesPerFormattersConfiguration = 60
	const formattersConfigurationEstimate = computed(() => {
		return MinutesPerFormattersConfiguration
	})

	const MinutesPerCiCdConfiguration = 240
	const ciCdConfigurationEstimate = computed(() => {
		return MinutesPerCiCdConfiguration
	})

	const MinutesPerDockerConfiguration = 120
	const dockerConfigurationEstimate = computed(() => {
		return MinutesPerDockerConfiguration
	})

	const MinutesPerKubernetesConfiguration = 240
	const kubernetesConfigurationEstimate = computed(() => {
		return MinutesPerKubernetesConfiguration
	})

	const MinutesPerTestingEnvironmentsConfiguration = 120
	const testingEnvironmentsConfigurationEstimate = computed(() => {
		return MinutesPerTestingEnvironmentsConfiguration
	})

	const MinutesPerEnvironmentVariablesAndConfigConfiguration = 120
	const environmentVariablesAndConfigConfigurationEstimate = computed(() => {
		return MinutesPerEnvironmentVariablesAndConfigConfiguration
	})

	const MinutesPerAuthenticationConfiguration = 120
	const authenticationConfigurationEstimate = computed(() => {
		return MinutesPerAuthenticationConfiguration
	})

	const MinutesPerReadmeAndDocumentation = 240
	const readmeAndDocumentationEstimate = computed(() => {
		return MinutesPerReadmeAndDocumentation
	})

	const databaseItems = computed(() => [
		{
			label: 'Tables/Migrations',
			count: tablesCount.value,
			per: MinutesPerTable,
			minutes: tablesEstimate.value,
		},
	])

	const apiItems = computed(() => [
		{
			label: 'Models',
			count: modelsCount.value,
			per: MinutesPerModel,
			minutes: modelsEstimate.value,
		},
		{
			label: 'Relations',
			count: relationsCount.value,
			per: MinutesPerRelation,
			minutes: relationsEstimate.value,
		},
		{
			label: 'Controllers',
			count: controllersCount.value,
			per: MinutesPerController,
			minutes: controllersEstimate.value,
		},
		{
			label: 'Routes',
			count: apiRoutesCount.value,
			per: MinutesPerApiRoute,
			minutes: apiRoutesEstimate.value,
		},
		{
			label: 'Requests',
			count: requestsCount.value,
			per: MinutesPerRequest,
			minutes: requestsEstimate.value,
		},
		{
			label: 'Factories',
			count: factoriesCount.value,
			per: MinutesPerFactory,
			minutes: factoriesEstimate.value,
		},
		{
			label: 'Seeders',
			count: seedersCount.value,
			per: MinutesPerSeeder,
			minutes: seedersEstimate.value,
		},
	])

	const uiItems = computed(() => [
		{
			label: 'List Views',
			count: listViewsCount.value,
			per: MinutesPerListView,
			minutes: listViewsEstimate.value,
		},
		{
			label: 'Edit Views',
			count: editViewsCount.value,
			per: MinutesPerEditView,
			minutes: editViewsEstimate.value,
		},
		{
			label: 'Forms',
			count: formsCount.value,
			per: MinutesPerForm,
			minutes: formsEstimate.value,
		},
		{
			label: 'Relation Widgets',
			count: relationWidgetsCount.value,
			per: MinutesPerRelationWidget,
			minutes: relationWidgetsEstimate.value,
		},
		{
			label: 'API Layers',
			count: apiLayersCount.value,
			per: MinutesPerApiLayer,
			minutes: apiLayersEstimate.value,
		},
		{
			label: 'Routes',
			count: uiRoutesCount.value,
			per: MinutesPerUiRoute,
			minutes: uiRoutesEstimate.value,
		},
	])

	const devEnvItems = computed(() => [
		{
			label: 'Basic environment setup',
			per: MinutesPerBasicEnvironmentSetup.toFixed(2),
			count: null,
			minutes: basicEnvironmentSetupEstimate.value,
		},
		{
			label: 'Linters configuration',
			per: MinutesPerLintersConfiguration.toFixed(2),
			count: null,
			minutes: lintersConfigurationEstimate.value,
		},
		{
			label: 'Formatters configuration',
			per: MinutesPerFormattersConfiguration.toFixed(2),
			count: null,
			minutes: formattersConfigurationEstimate.value,
		},
		{
			label: 'CI/CD configuration',
			per: MinutesPerCiCdConfiguration.toFixed(2),
			count: null,
			minutes: ciCdConfigurationEstimate.value,
		},
		{
			label: 'Docker configuration',
			per: MinutesPerDockerConfiguration.toFixed(2),
			count: null,
			minutes: dockerConfigurationEstimate.value,
		},
		{
			label: 'Kubernetes configuration',
			per: MinutesPerKubernetesConfiguration.toFixed(2),
			count: null,
			minutes: kubernetesConfigurationEstimate.value,
		},
		{
			label: 'Testing environments configuration',
			per: MinutesPerTestingEnvironmentsConfiguration.toFixed(2),
			count: null,
			minutes: testingEnvironmentsConfigurationEstimate.value,
		},
		{
			label: 'Environment variables & config',
			per: MinutesPerEnvironmentVariablesAndConfigConfiguration.toFixed(2),
			count: null,
			minutes: environmentVariablesAndConfigConfigurationEstimate.value,
		},
		{
			label: 'Authentication configuration',
			per: MinutesPerAuthenticationConfiguration.toFixed(2),
			count: null,
			minutes: authenticationConfigurationEstimate.value,
		},
		{
			label: 'Readme and documentation',
			per: MinutesPerReadmeAndDocumentation.toFixed(2),
			count: null,
			minutes: readmeAndDocumentationEstimate.value,
		},
	])

	return {
		LaravelDeveloperHourlyRate,
		VueDeveloperHourlyRate,
		CombinedDeveloperHourlyRate,
		ConsultingRate,
		totalConsultingValue,
		totalDatabaseEstimate,
		totalDatabaseValue,
		totalApiEstimate,
		totalApiValue,
		totalUiEstimate,
		totalUiValue,
		totalDevEnvironmentEstimate,
		totalDevEnvironmentValue,
		totalDevelopmentTime,
		totalDevelopmentValue,
		MinutesPerTable,
		tablesCount,
		tablesEstimate,
		MinutesPerRelationWidget,
		relationWidgetsCount,
		relationWidgetsEstimate,
		MinutesPerRelation,
		relationsCount,
		relationsEstimate,
		MinutesPerModel,
		modelsCount,
		modelsEstimate,
		MinutesPerController,
		controllersCount,
		controllersEstimate,
		MinutesPerApiRoute,
		apiRoutesCount,
		apiRoutesEstimate,
		MinutesPerRequest,
		requestsCount,
		requestsEstimate,
		MinutesPerFactory,
		factoriesCount,
		factoriesEstimate,
		MinutesPerSeeder,
		seedersCount,
		seedersEstimate,
		MinutesPerListView,
		listViewsCount,
		listViewsEstimate,
		MinutesPerEditView,
		editViewsCount,
		editViewsEstimate,
		MinutesPerForm,
		formsCount,
		formsEstimate,
		MinutesPerApiLayer,
		apiLayersCount,
		apiLayersEstimate,
		MinutesPerUiRoute,
		uiRoutesCount,
		uiRoutesEstimate,
		MinutesPerBasicEnvironmentSetup,
		basicEnvironmentSetupEstimate,
		MinutesPerLintersConfiguration,
		lintersConfigurationEstimate,
		MinutesPerFormattersConfiguration,
		formattersConfigurationEstimate,
		MinutesPerCiCdConfiguration,
		ciCdConfigurationEstimate,
		MinutesPerDockerConfiguration,
		dockerConfigurationEstimate,
		MinutesPerKubernetesConfiguration,
		kubernetesConfigurationEstimate,
		MinutesPerTestingEnvironmentsConfiguration,
		testingEnvironmentsConfigurationEstimate,
		MinutesPerEnvironmentVariablesAndConfigConfiguration,
		environmentVariablesAndConfigConfigurationEstimate,
		MinutesPerAuthenticationConfiguration,
		authenticationConfigurationEstimate,
		MinutesPerReadmeAndDocumentation,
		readmeAndDocumentationEstimate,
		databaseItems,
		apiItems,
		uiItems,
		devEnvItems,
	}
}
